import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Dayjs } from "dayjs";
import dayjs from "dayjs";
import "dayjs/locale/th";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import {
  getETaxAutoSendAlerts,
  getETaxAutoSendSettings,
  getETaxSummary,
} from "../services";
import { ETaxSummaryResponse } from "../types/e-tax.type";
import {
  EtaxAutoSendAlerts,
  EtaxAutoSendSettings,
} from "../types/etax-auto-send.type";
import SummaryCards from "../components/ETax/SummaryCards";
import AlertsStrip from "../components/ETax/AutoSend/AlertsStrip";
import SettingsDialog from "../components/ETax/AutoSend/SettingsDialog";
import DateRangePicker, {
  etaxPresetGroups,
} from "../components/common/DateRangePicker";
import {
  buildDateRangeSearch,
  formatDateParam,
  isDateRangeInvalid,
  resolveInitialDateRange,
} from "../components/ETax/dateRangeParams";
import { CHANNEL } from "../components/ETax/documentMeta";
import { buildAlertCardPath } from "../components/ETax/AutoSend/autoSendMeta";
import { ETAX_TITLE, getEtaxMonthSuffix } from "../components/ETax/etaxTitle";
import { COMPACT_QUERY } from "../utils/hooks/useCompact";

const compact = "@media " + COMPACT_QUERY;

dayjs.locale("th");

/** Focus and visibilitychange both fire when a tab comes back; one reload is enough. */
const REFOCUS_RELOAD_GAP_MS = 2000;

export default function ETaxPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // The range lives in the URL so it survives navigating into the detail / send /
  // history pages and back again — those screens link to `/e-tax${dateSearch}`, and
  // without this the dashboard would silently reset to "all dates" while the page the
  // user just came from was still filtered. With no params at all it opens on the
  // current month (1st → today) rather than unfiltered.
  const initialRange = useRef(
    resolveInitialDateRange(
      searchParams.get("date_from"),
      searchParams.get("date_to"),
    ),
  ).current;
  const [startDate, setStartDate] = useState<Dayjs>(initialRange.from);
  const [endDate, setEndDate] = useState<Dayjs>(initialRange.to);
  const presetGroups = useMemo(() => etaxPresetGroups(), []);

  const handleChangeDateRange = (start: Dayjs, end: Dayjs) => {
    setStartDate(start);
    setEndDate(end);
  };

  const rangeInvalid = isDateRangeInvalid(startDate, endDate);

  const [summary, setSummary] = useState<ETaxSummaryResponse>();
  const [loading, setLoading] = useState<boolean>(false);
  const [summaryError, setSummaryError] = useState<boolean>(false);

  // Only the newest request may write state. Picking a range fires one request per
  // picker change, and without this guard a slower earlier response (e.g. the
  // start-date-only request) can land last and leave the cards showing a range the
  // user is no longer looking at.
  const requestSeq = useRef<number>(0);

  const dateSearch = buildDateRangeSearch(startDate, endDate);

  // Both effects below reach the URL through refs: the setter's identity can change with the
  // URL, and an effect depending on it would also run on navigations it must not react to.
  const searchParamsRef = useRef(searchParams);
  searchParamsRef.current = searchParams;
  const setSearchParamsRef = useRef(setSearchParams);
  setSearchParamsRef.current = setSearchParams;

  // URL -> state. Our own write below always carries both bounds, so a URL that differs from
  // the pickers came from outside: the back button, a pasted link, or the E-Tax sidebar link
  // clicked while already here (no params at all), which resets to the default range.
  const dateFromParam = searchParams.get("date_from");
  const dateToParam = searchParams.get("date_to");
  useEffect(() => {
    const next = resolveInitialDateRange(dateFromParam, dateToParam);
    setStartDate((prev) =>
      formatDateParam(prev) === formatDateParam(next.from) ? prev : next.from,
    );
    setEndDate((prev) =>
      formatDateParam(prev) === formatDateParam(next.to) ? prev : next.to,
    );
    // A bound was missing or the bounds were swapped: put the range actually shown back into
    // the URL (the write below does not run when the pickers already held that range).
    const normalized = buildDateRangeSearch(next.from, next.to).slice(1);
    const fromUrl = new URLSearchParams();
    if (dateFromParam) fromUrl.append("date_from", dateFromParam);
    if (dateToParam) fromUrl.append("date_to", dateToParam);
    if (fromUrl.toString() !== normalized) {
      setSearchParamsRef.current(normalized, { replace: true });
    }
  }, [dateFromParam, dateToParam]);

  // State -> URL. Runs only when the pickers change, never on a URL change, so it cannot
  // write the old range back over a navigation the effect above is about to apply.
  useEffect(() => {
    const current = `?${searchParamsRef.current.toString()}`;
    const next = dateSearch || "?";
    if (current !== next) {
      setSearchParamsRef.current(dateSearch ? dateSearch.slice(1) : "", { replace: true });
    }
  }, [dateSearch]);

  const fetchSummary = useCallback(async () => {
    // A start-after-end range matches nothing. Returning zeros here would be
    // indistinguishable from "this period has no documents", so surface the error on
    // the pickers instead and leave the previous figures alone.
    if (rangeInvalid) return;

    const seq = requestSeq.current + 1;
    requestSeq.current = seq;

    setLoading(true);
    setSummaryError(false);
    try {
      const {
        data: { data },
      } = await getETaxSummary({ dateFrom: startDate, dateTo: endDate });
      if (requestSeq.current !== seq) return;
      setSummary(data);
    } catch {
      // The axios interceptor surfaces the error toast; reset to an empty state
      // so the cards are never stuck on the loading skeleton.
      if (requestSeq.current !== seq) return;
      setSummary(undefined);
      setSummaryError(true);
    } finally {
      if (requestSeq.current === seq) setLoading(false);
    }
  }, [startDate, endDate, rangeInvalid]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  // ---- weekly auto-send: alerts strip + settings ----
  // The held / expired counts follow the date range (the other alert counts stay company-wide,
  // server-side), so the alerts re-fetch with every range change. Settings load once.
  const [alerts, setAlerts] = useState<EtaxAutoSendAlerts>();
  const [autoSendSettings, setAutoSendSettings] = useState<EtaxAutoSendSettings>();
  const [alertsLoading, setAlertsLoading] = useState<boolean>(false);
  const [alertsError, setAlertsError] = useState<boolean>(false);
  const [settingsOpen, setSettingsOpen] = useState<boolean>(false);
  // Same newest-request-wins guard as the summary: a picker edit fires one request each.
  const alertsRequestSeq = useRef<number>(0);

  // `silent` keeps the figures on screen (no skeleton, no error swap) for a background refresh
  // when the tab regains focus.
  const fetchAlerts = useCallback(
    async (silent = false) => {
      // An inverted range matches nothing; keep the previous figures, as the summary does.
      if (rangeInvalid) return;

      const seq = alertsRequestSeq.current + 1;
      alertsRequestSeq.current = seq;

      if (!silent) {
        setAlertsLoading(true);
        setAlertsError(false);
      }
      try {
        const {
          data: { data },
        } = await getETaxAutoSendAlerts({ dateFrom: startDate, dateTo: endDate });
        if (alertsRequestSeq.current !== seq) return;
        setAlerts(data);
        setAlertsError(false);
      } catch {
        // Surfaced by the axios interceptor; the strip says the load failed.
        if (alertsRequestSeq.current !== seq || silent) return;
        setAlerts(undefined);
        setAlertsError(true);
      } finally {
        if (alertsRequestSeq.current === seq) setAlertsLoading(false);
      }
    },
    [startDate, endDate, rangeInvalid],
  );

  const fetchAutoSendSettings = useCallback(async () => {
    try {
      const {
        data: { data },
      } = await getETaxAutoSendSettings();
      setAutoSendSettings(data);
    } catch {
      // Surfaced by the axios interceptor.
      setAutoSendSettings(undefined);
    }
  }, []);

  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  useEffect(() => {
    fetchAutoSendSettings();
  }, [fetchAutoSendSettings]);

  // The next round moves when a round runs or someone edits the schedule in another tab or
  // on the rounds page, so the strip re-reads settings and alerts whenever the page is looked
  // at again instead of keeping the time it had when it was first opened. (Coming back from
  // another E-Tax screen remounts this page, which loads both afresh anyway.)
  const lastRefocusReload = useRef<number>(0);
  useEffect(() => {
    const reload = () => {
      if (document.visibilityState === "hidden") return;
      const now = Date.now();
      if (now - lastRefocusReload.current < REFOCUS_RELOAD_GAP_MS) return;
      lastRefocusReload.current = now;
      fetchAutoSendSettings();
      fetchAlerts(true);
    };
    window.addEventListener("focus", reload);
    document.addEventListener("visibilitychange", reload);
    return () => {
      window.removeEventListener("focus", reload);
      document.removeEventListener("visibilitychange", reload);
    };
  }, [fetchAutoSendSettings, fetchAlerts]);

  const handleSettingsSaved = (saved: EtaxAutoSendSettings) => {
    setAutoSendSettings(saved);
    // The strip's next-round line and scheduled counts follow the new schedule.
    fetchAlerts();
  };

  // (paper) documents, carrying the active date range alongside the channel filter.
  // Was `payment_type=cashsale`, which selected cash bills and called them paper. The two
  // stopped being the same thing the moment a shop could ask for an electronic invoice
  // while still paying cash, and the card behind this link no longer counts them that way.
  const paperSearch = dateSearch
    ? `${dateSearch}&channel=${CHANNEL.PAPER}`
    : `?channel=${CHANNEL.PAPER}`;

  return (
    <Box sx={{ padding: "8px 0" }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
        sx={{ [compact]: { flexWrap: "wrap", gap: 1 } }}
      >
        <Typography variant="h6" fontWeight={700}>
          {ETAX_TITLE}{" "}
          <Box component="span" sx={{ fontWeight: 400 }}>
            {getEtaxMonthSuffix(startDate, endDate)}
          </Box>
        </Typography>
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="body2" color="text.secondary">
            วันที่
          </Typography>
          <Box sx={{ minWidth: 280 }}>
            <DateRangePicker
              dateFrom={startDate}
              dateTo={endDate}
              onChange={handleChangeDateRange}
              presetGroups={presetGroups}
            />
          </Box>
        </Box>
      </Box>

      <AlertsStrip
        alerts={alerts}
        settings={autoSendSettings}
        loading={alertsLoading}
        error={alertsError}
        onRetry={() => fetchAlerts()}
        // Held / expired open on the dashboard's range, the company-wide cards on no range, so
        // each list holds exactly the rows its card counted.
        onSelectCard={(card) => navigate(buildAlertCardPath(card, startDate, endDate))}
        onOpenSettings={() => setSettingsOpen(true)}
        onOpenRounds={() => navigate(`/e-tax/auto-send${dateSearch}`)}
      />

      <SummaryCards
        summary={summary}
        loading={loading}
        error={summaryError}
        onRetry={fetchSummary}
        onViewDetail={() => navigate(`/e-tax/details${dateSearch}`)}
        onViewPaper={() => navigate(`/e-tax/details${paperSearch}`)}
        onViewHistory={() => navigate(`/e-tax/history${dateSearch}`)}
        onSend={() => navigate(`/e-tax/send${dateSearch}`)}
      />

      <SettingsDialog
        open={settingsOpen}
        settings={autoSendSettings}
        onClose={() => setSettingsOpen(false)}
        onSaved={handleSettingsSaved}
      />
    </Box>
  );
}
